import React, { useState, useRef, useEffect } from 'react';
import Modal from 'react-modal';
import { 
    X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize, 
    Download, Trash2, FolderInput, Star, Edit2
} from 'lucide-react';
import './ImageViewerModal.css';

const ImageViewerModal = ({
  isOpen, onRequestClose, imageUrl, imageAlt, title,
  onNext, onPrevious, canGoNext, canGoPrevious,
  onDownload, folders, currentFolderId, onMoveToFolder, onDelete,
  isFavorite, onToggleFavorite,
  // Rename logic
  isRenaming, renameValue, setRenameValue, onStartRename, onSaveRename, onCancelRename
}) => {

  // --- ZOOM & PAN STATE ---
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // --- REFS (FIXED) ---
  const imageRef = useRef(null); 

  // --- TOUCH STATE (SWIPE) ---
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minZoom = 0.5;
  const maxZoom = 5;

  // Reset on image change
  useEffect(() => {
      setZoom(1);
      setPan({ x: 0, y: 0 });
  }, [imageUrl]);

  // --- ZOOM HANDLERS ---
  const handleZoom = (delta) => {
      setZoom(prev => Math.min(Math.max(prev + delta, minZoom), maxZoom));
  };

  const handleWheel = (e) => {
      e.stopPropagation();
      // Simple scroll zoom
      const delta = e.deltaY * -0.001; 
      setZoom(prev => Math.min(Math.max(prev + delta, minZoom), maxZoom));
  };

  const handleDoubleClick = () => {
      if (zoom > 1) {
          setZoom(1); setPan({ x: 0, y: 0 }); // Reset
      } else {
          setZoom(2); // Quick Zoom
      }
  };

  // --- DRAG / PAN HANDLERS ---
  const handleMouseDown = (e) => {
      if (zoom > 1) {
          e.preventDefault();
          setIsDragging(true);
          setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      }
  };

  const handleMouseMove = (e) => {
      if (isDragging && zoom > 1) {
          e.preventDefault();
          setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
      }
  };

  const handleMouseUp = () => setIsDragging(false);

  // --- SWIPE GESTURES (Mobile) ---
  const onTouchStart = (e) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
      if (!touchStart || !touchEnd) return;
      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > 50;
      const isRightSwipe = distance < -50;
      
      if (isLeftSwipe && canGoNext) onNext();
      if (isRightSwipe && canGoPrevious) onPrevious();
  };

  // --- RENAME HANDLERS (Enter/Esc) ---
  const handleRenameKeyDown = (e) => {
      if (e.key === 'Enter') onSaveRename();
      if (e.key === 'Escape') onCancelRename();
  };

  const modalStyles = {
    overlay: { 
        backgroundColor: 'rgba(0, 0, 0, 0.92)', 
        backdropFilter: 'blur(8px)',
        zIndex: 9999, 
        padding: 0
    },
    content: { 
        position: 'absolute', inset: 0, border: 'none', background: 'transparent', padding: 0, overflow: 'hidden'
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} style={modalStyles} contentLabel="Image Viewer">
      <div className="viewer-container">
        
        {/* --- 1. TOP BAR (Minimal) --- */}
        <div className="viewer-top-bar">
            <div className="top-left-info mobile-only"></div>
            <button onClick={onRequestClose} className="btn-close-circle" title="Close">
                <X size={24} />
            </button>
        </div>

        {/* --- 2. IMAGE STAGE (Hero) --- */}
        <div 
            className="viewer-stage"
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onDoubleClick={handleDoubleClick}
        >
            {/* Desktop Arrows */}
            {canGoPrevious && (
                <button onClick={onPrevious} className="nav-arrow left desktop-only"><ChevronLeft size={40} /></button>
            )}

            <div className="image-wrapper" style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}>
                <img 
                    ref={imageRef}
                    src={imageUrl} 
                    alt={imageAlt} 
                    style={{ 
                        transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                    }}
                    className="cinematic-image"
                    draggable={false} 
                />
            </div>

            {canGoNext && (
                <button onClick={onNext} className="nav-arrow right desktop-only"><ChevronRight size={40} /></button>
            )}
        </div>

        {/* --- 3. BOTTOM DOCK (Glass Command Center) --- */}
        <div className="viewer-dock-wrapper">
            <div className="glass-dock">
                
                {/* A. Info & Rename Group */}
                <div className="dock-section main-info">
                    <button 
                        className={`btn-dock-icon ${isFavorite ? 'active-star' : ''}`} 
                        onClick={onToggleFavorite}
                        title="Toggle Favorite"
                    >
                        <Star size={20} fill={isFavorite ? "#eab308" : "none"} />
                    </button>

                    <div className="title-edit-wrapper">
                        {isRenaming ? (
                            <input 
                                className="rename-inline-input"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={handleRenameKeyDown}
                                onBlur={onSaveRename}
                                autoFocus
                            />
                        ) : (
                            <span 
                                className="dock-title" 
                                onClick={onStartRename} 
                                title="Click to rename"
                            >
                                {title || 'Untitled Design'}
                            </span>
                        )}
                    </div>
                </div>

                <div className="dock-divider"></div>

                {/* B. Action Group */}
                <div className="dock-section actions">
                    <div className="folder-dropdown-wrapper">
                        <FolderInput size={20} className="dock-icon-muted"/>
                        <select 
                            className="dock-select"
                            value={currentFolderId === null || currentFolderId === undefined ? 'unfiled' : currentFolderId}
                            onChange={(e) => onMoveToFolder(e.target.value === 'unfiled' ? null : parseInt(e.target.value))}
                        >
                            <option value="unfiled">Unfiled</option>
                            {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                    </div>

                    <button onClick={onDownload} className="btn-dock-action" title="Download">
                        <Download size={20} />
                    </button>
                    <button onClick={onDelete} className="btn-dock-action danger" title="Delete">
                        <Trash2 size={20} />
                    </button>
                </div>

                <div className="dock-divider desktop-only"></div>

                {/* C. Zoom Controls (Desktop) */}
                <div className="dock-section zoom-controls desktop-only">
                    <button onClick={() => handleZoom(-0.25)} className="btn-zoom"><ZoomOut size={16}/></button>
                    <span className="zoom-text">{Math.round(zoom * 100)}%</span>
                    <button onClick={() => handleZoom(0.25)} className="btn-zoom"><ZoomIn size={16}/></button>
                    <button onClick={() => {setZoom(1); setPan({x:0,y:0})}} className="btn-zoom" title="Fit"><Maximize size={14}/></button>
                </div>

            </div>
        </div>

      </div>
    </Modal>
  );
};

export default ImageViewerModal;